import { createClient } from 'microcms-js-sdk';

/**
 * Class NewsMedia
 */
class NewsMedia {
	transition: number;
	limit: number;
	isFirstLoad: boolean; // 初回ロード判定用フラグ
	pageType: string;
	qParam: string;
	newsYearEarliest: number;
	loadingElement: JQuery;
	block: JQuery;
	newsContainer: JQuery;
	newsKeywords: JQuery;
	newsKeywordsButton: JQuery;
	newsYear: JQuery;
	newsLanguage: string;
	newsRegion: string;
	newsPageType: string;
	client: ReturnType<typeof createClient>;

	constructor(element: JQuery) {
		this.block = element;
		this.pageType = (
			this.block.find('#js_news_pagetype') as JQuery<HTMLInputElement>
		).val() as string;
		this.transition = 400;
		this.limit = this.pageType === 'index' ? 5 : 10;
		this.isFirstLoad = true;
		this.qParam = '';
		this.newsYearEarliest = 2020;
		this.loadingElement = this.block.find('#js_loading');
		this.newsContainer = this.block.find('#js_news_container');
		this.newsKeywords = this.block.find('#js_news_keywords');
		this.newsKeywordsButton = this.block.find('#js_news_keywords_button');
		this.newsYear = this.block.find('#js_news_year');
		this.newsLanguage =
			(this.block.find('#js_news_language') as JQuery<HTMLInputElement>)?.val() || '';
		this.newsRegion =
			(this.block.find('#js_news_region') as JQuery<HTMLInputElement>)?.val() || '';
		this.newsPageType =
			(this.block.find('#js_news_pagetype') as JQuery<HTMLInputElement>)?.val() || '';

		// microCMSクライアントの初期化
		this.client = createClient({
			serviceDomain: '3kxo8u9nf1', // microCMSのサービスドメイン
			apiKey: 'c6086e40236a405c9410b8c52bed50b428f0', //画面プレビュー用
			// apiKey: 'ac14fd9441e74441bf3d27c001e7ddb131fb', //本番用
		});

		this.init();
	}

	/**
	 * Initialize
	 */
	init() {
		this.initializeEventListeners();

		if (this.pageType === 'list') {
			this.initializeYearSelect();
			this.initializeNews();
		} else {
			this.fetchNews(1);
		}
	}

	/**
	 * 初期化処理: 年選択リスト生成
	 */
	initializeYearSelect() {
		const currentYear = new Date().getFullYear();
		let yearlists = '';
		for (let i = currentYear; i >= this.newsYearEarliest; i--) {
			yearlists += `<option value="${i}">${i}</option>`;
		}
		this.newsYear.append(yearlists);
	}

	/**
	 * 初期化処理
	 * 初回ロード時にクエリパラメータからページ番号、qを取得
	 */
	initializeNews() {
		const urlParams = new URLSearchParams(window.location.search);
		const pageParam = urlParams.get('page');
		const page = pageParam ? parseInt(pageParam, 10) : 1; // クエリパラメータからpageを取得 (デフォルト: 1)
		this.qParam = urlParams.get('q') || ''; // クエリパラメータqをクラス変数に代入
		// qクエリパラメータが存在する場合、検索窓に値を設定
		if (this.qParam) {
			const searchInput = this.newsKeywords as JQuery<HTMLInputElement>;
			if (searchInput.length) {
				searchInput.val(this.qParam);
			}
		}

		this.fetchNews(page); // 初回ロード時に適切なページ番号でリクエスト
	}

	/**
	 * Set up event listeners
	 */
	initializeEventListeners() {
		// Enterキーで検索
		this.newsKeywords.on('keypress', (event) => {
			if (event.key === 'Enter') {
				if (this.pageType === 'list') {
					event.preventDefault();
				}
				this.fetchNews(1);
			}
		});

		// 検索ボタンで検索
		this.newsKeywordsButton.on('click', () => {
			this.fetchNews(1);
		});

		// Yearでフィルタリング
		this.newsYear.on('change', () => {
			this.fetchNews(1);
		});
	}

	/**
	 * Fetch products from the API
	 * @param page
	 */
	async fetchNews(page: number) {
		let keyword = '';

		if (this.pageType === 'list') {
			// キーワードの最新値を取得し、空の場合はqParamをリセット
			keyword = (this.newsKeywords as JQuery<HTMLInputElement>)?.val() || '';
			this.qParam = keyword || ''; // 検索窓が空の場合にqParamをリセット

			// URLを更新 (qパラメータを反映)
			const url = new URL(window.location.href);
			if (this.qParam) {
				url.searchParams.set('q', this.qParam); // 検索キーワードを更新
			} else {
				url.searchParams.delete('q'); // qが空の場合は削除
			}
			url.searchParams.set('page', page.toString()); // ページ番号を更新
			history.replaceState(null, '', url.toString()); // アドレスバーを更新
		}

		// 年選択値を取得
		const selectedYear = ((this.newsYear as JQuery<HTMLSelectElement>).val() as string) || '';

		// filters設定
		const filters = this.createFilterQuery(this.newsRegion, selectedYear);
		// console.log(filters);

		// loadingを表示
		this.newsContainer.removeClass('is_show');
		this.loadingElement.addClass('is_show');

		// APIリクエスト
		try {
			const data = await this.client.get({
				endpoint: 'toray_news',
				queries: {
					filters,
					q: keyword, // キーワードを指定
					orders: '-date,-publishedAt',
					fields: 'id,date,language,region,newsCategory,title,directLink,thumbnail',
					limit: this.limit,
					offset: (page - 1) * this.limit,
				},
			});
			this.updateNewsGrid(data);
		} catch (error) {
			console.error('Error fetching news:', error);
		} finally {
			// loading非表示
			setTimeout(() => {
				this.newsContainer.addClass('is_show');
				this.loadingElement.removeClass('is_show');
			}, this.transition);
		}
	}

	/**
	 * Create filter query
	 * @param newsRegion
	 * @param selectedYear
	 */
	createFilterQuery(newsRegion: string, selectedYear: string): string {
		const regionFilter = newsRegion ? `region[contains]${newsRegion}` : '';
		let yearFilter = '';

		if (selectedYear) {
			yearFilter = `date[contains]${selectedYear}`;
		} else {
			// 年が選択されていない場合、2020年以降の記事を取得
			yearFilter = `date[greater_than]2019-12-31`;
		}

		// `regionFilter` と `yearFilter` のいずれかが存在しない場合も安全に処理
		return [regionFilter, yearFilter].filter(Boolean).join('[and]');
	}

	/**
	 * Update product grid with API data
	 * @param data - APIレスポンスデータ
	 */
	updateNewsGrid(data: any) {
		this.newsContainer.empty();

		// レスポンスのtotalCountが0の場合
		if (data.totalCount === 0) {
			const noResultMessage = `
				<div class="no-result-message">
					<p>There is no relevant news.</p>
				</div>
			`;
			this.newsContainer.html(noResultMessage);

			// ページネーション要素を非表示にする
			this.block.find('.pagination').hide();
			return; // 処理を終了
		}

		// ページネーション要素を表示する
		this.block.find('.pagination').show();

		// レスポンスデータがある場合はグリッドを作成
		data.contents.forEach((item: any) => {
			const itemId = item.id;
			const itemDate = new Date(item.date);
			const formattedDate = new Intl.DateTimeFormat('en-US', {
				month: '2-digit',
				day: '2-digit',
				year: '2-digit',
			})
				.format(itemDate)
				.replace(/\//g, ' / ');
			const isoDate = itemDate.toISOString();

			const thumbnail = item.thumbnail?.url || '/us/assets/images/thumb_noimage.jpg';
			const title = item.title;
			const url = item.directLink || `/us/media-news/article.html?contentId=${itemId}`;
			const target = item.directLink ? 'target="_blank" rel="noopener noreferrer"' : '';

			let categoryHtml = '';
			if (item.newsCategory) {
				categoryHtml = `<li><span>${item.newsCategory.name_en}</span></li>`;
			} else {
				categoryHtml = '<li><span>News</span></li>';
			}

			const newsCard = `
					<a class="news-card" href="${url}" ${target}>
							<figure class="news-card__figure">
											<img
													fetchpriority="high"
													decoding="async"
													width="640"
													height="400"
													src="${thumbnail}"
													class="news-card__image"
													alt=""
													sizes="(max-width: 640px) 100vw, 640px"
											/>
							</figure>
							<div class="news-card__content">
									<ul class="news-card__categories">
											${categoryHtml}
									</ul>
									<h2 class="news-card__title has-t-5-bold-font-size">
									${title}
									</h2>
							</div>
							<div class="news-card__date">
									<p><time datetime="${isoDate}">${formattedDate}</time>
									</p>
							</div>
					</a>
			`;

			this.newsContainer.append(newsCard);
		});

		if (this.pageType === 'list') {
			// ページネイションを生成
			const currentPage = Math.floor(data.offset / data.limit) + 1; // 現在のページを計算
			this.generatePagination(currentPage, data.limit, data.offset, data.totalCount);

			// 初回ロード以外の場合はスクロール
			if (!this.isFirstLoad) {
				window.scrollTo({
					top: this.block.offset()?.top || 0,
				});
			}

			// 初回ロード後はフラグをfalseに変更
			this.isFirstLoad = false;
		}
	}

	/**
	 * Generate pagination and append it to the DOM
	 * @param currentPage - 現在のページ番号
	 * @param limit       - 1ページあたりの表示件数
	 * @param offset      - 現在のデータの開始位置
	 * @param totalCount  - 総データ件数
	 */
	generatePagination(currentPage: number, limit: number, offset: number, totalCount: number) {
		const totalPages = Math.ceil(totalCount / limit);
		const paginationElement = this.block.find('#js_products_pagination');
		paginationElement.empty();

		if (totalPages <= 1) return;

		const generatePageUrl = (page: number): string => `./?page=${page}`;

		const prevPage = currentPage > 1 ? currentPage - 1 : null;
		const nextPage = currentPage < totalPages ? currentPage + 1 : null;

		// ページ番号の範囲を計算
		let startPage = Math.max(1, currentPage - 2);
		const endPage = Math.min(totalPages, startPage + 4);

		// 範囲を調整
		if (endPage - startPage < 4) {
			startPage = Math.max(1, endPage - 4);
		}

		let pageLinks = '';

		// 最初のページへのリンク（必要な場合）
		if (startPage > 1) {
			pageLinks += `<a href="${generatePageUrl(1)}" data-page="1"><span class="pagination__number">1</span></a>`;
			if (startPage > 2) {
				pageLinks += '<span class="pagination__ellipsis">...</span>';
			}
		}

		// ページ番号リンクの生成
		for (let i = startPage; i <= endPage; i++) {
			const pageUrl = generatePageUrl(i);
			const activeClass = i === currentPage ? 'active' : '';
			pageLinks += `<a href="${pageUrl}" data-page="${i}"><span class="pagination__number ${activeClass}">${i}</span></a>`;
		}

		// 最後のページへのリンク（必要な場合）
		if (endPage < totalPages) {
			if (endPage < totalPages - 1) {
				pageLinks += '<span class="pagination__ellipsis">...</span>';
			}
			pageLinks += `<a href="${generatePageUrl(totalPages)}" data-page="${totalPages}"><span class="pagination__number">${totalPages}</span></a>`;
		}

		// ページネーションHTML
		const paginationHtml = `
        <div class="pagination">
            <div class="pagination__left">
                <button class="pagination__arrow ${!prevPage ? 'disabled' : ''}" data-page="${prevPage || ''}" ${!prevPage ? 'disabled' : ''}>
                    <i class="icon-arrow-left"></i>
                </button>
            </div>
            <div class="pagination__center">
                ${pageLinks}
            </div>
            <div class="pagination__right">
                <button class="pagination__arrow ${!nextPage ? 'disabled' : ''}" data-page="${nextPage || ''}" ${!nextPage ? 'disabled' : ''}>
                    <i class="icon-arrow-right"></i>
                </button>
            </div>
        </div>
    `;

		paginationElement.append(paginationHtml);

		// ページネーションクリックイベントを設定
		paginationElement.find('a, button').on('click', (event) => {
			event.preventDefault();
			const targetPage = $(event.currentTarget).data('page');
			if (targetPage) {
				this.fetchNews(targetPage);
			}
		});
	}

	/**
	 * Debounce helper function
	 * @param func
	 * @param wait
	 */
	debounce(func: () => void, wait: number): () => void {
		let timeout: number;
		return () => {
			clearTimeout(timeout);
			timeout = window.setTimeout(func, wait);
		};
	}
}

// クラスインスタンスの作成
const newsMedia: NewsMedia[] = [];
$('#js_news_block').each(function () {
	const newsMediaInstance = new NewsMedia($(this));
	newsMedia.push(newsMediaInstance);
});
